import {atom, useAtom, useAtomValue, useSetAtom} from 'jotai'
import {ScopeProvider} from 'jotai-scope'
import {type ComponentType, memo, type PropsWithChildren, type ReactNode, useMemo} from 'react'
import type {AsyncState} from '../@types/asyncState'
import type {Setter} from '../@types/setter'
import {AsyncStateInit} from '../enums/asyncState'
import {useOnce} from '../hooks/useOnce'

const asyncAtom = atom<AsyncState>(AsyncStateInit)

export function useStateAsyncState(): [AsyncState, Setter<AsyncState>] {
  return useAtom(asyncAtom)
}

export function useAsyncState(): AsyncState {
  return useAtomValue(asyncAtom)
}

export function useSetAsyncState(): Setter<AsyncState> {
  return useSetAtom(asyncAtom)
}

interface ScopeInitializerProps {
  initialValue: AsyncState
}

const ScopeInitializer = memo(({initialValue}: ScopeInitializerProps) => {
  const setAsyncState = useSetAsyncState()
  useOnce(() => {
    setAsyncState(initialValue)
  })
  return null
})

interface AsyncContextProps extends PropsWithChildren {
  initialValue?: AsyncState
}

export const AsyncContext = memo(({children, initialValue = AsyncStateInit}: AsyncContextProps) => {
  const atoms = useMemo(() => [asyncAtom], [])

  return <ScopeProvider atoms={atoms}>
    <ScopeInitializer initialValue={initialValue}/>
    {children}
  </ScopeProvider>
})

export function withAsyncContext<T extends object>(Component: ComponentType<T>) {
  return (props: T): ReactNode => {
    return <AsyncContext>
      <Component {...props} />
    </AsyncContext>
  }
}
